import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { listChannels } from "@kritaka/core";
import { useEffect, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
export function ChannelList({ workspaceId, selectedChannelId, onSelect, unreadCounts, mentionCounts, draftChannelIds, filter = "public", excludeChannelIds, onContextRequest, threadsByChannel, selectedThreadId, onSelectThread, }) {
    const ctx = useDataContext();
    const [channels, setChannels] = useState(null);
    const [error, setError] = useState(null);
    // KTK-397 — which channels have their thread children expanded. Collapsed
    // is the default so the sidebar stays scannable.
    const [expandedThreads, setExpandedThreads] = useState(new Set());
    useEffect(() => {
        let cancelled = false;
        setChannels(null);
        setError(null);
        listChannels(ctx, workspaceId)
            .then((cs) => {
            if (!cancelled)
                setChannels(cs);
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [ctx, workspaceId]);
    if (error)
        return _jsxs("div", { style: { color: "var(--error)" }, children: ["Hub error: ", error] });
    if (!channels)
        return _jsx("div", { style: { color: "var(--text-muted)" }, children: "Loading channels\u2026" });
    // Scout channels surface via ScoutList so we don't duplicate the device in
    // both places. `visibility` defaults to `public` for legacy rows.
    const visible = channels.filter((c) => !c.scout_instance_id &&
        (c.visibility ?? "public") === filter &&
        !(excludeChannelIds?.has(c.id) ?? false));
    if (visible.length === 0)
        return (_jsx("div", { style: { color: "var(--text-muted)", fontSize: 12, padding: "4px 8px" }, children: filter === "bullpen" ? "No editorial channels." : "No channels." }));
    return (_jsx("ul", { style: { listStyle: "none", padding: 0, margin: 0 }, children: visible.map((c) => {
            const selected = c.id === selectedChannelId;
            const unread = unreadCounts?.[c.id] ?? 0;
            const mentions = mentionCounts?.[c.id] ?? 0;
            const decisionCount = c.pending_decisions_count ?? 0;
            const hasDraft = draftChannelIds?.has(c.id) ?? false;
            // KTK-274 — single trailing badge slot, priority: decision > mention > unread.
            // Avoids stacking three badges on one row when a channel has all three.
            let badge = null;
            if (decisionCount > 0) {
                badge = _jsx(ChannelBadge, { variant: "decision", title: `${decisionCount} decisions waiting`, children: decisionCount > 9 ? "9+" : decisionCount });
            }
            else if (mentions > 0) {
                badge = _jsx(ChannelBadge, { variant: "mention", children: mentions > 99 ? "99+" : mentions });
            }
            else if (unread > 0 && !selected) {
                badge = _jsx(ChannelBadge, { variant: "unread", children: unread > 99 ? "99+" : unread });
            }
            return (_jsxs("li", { children: [_jsxs("button", { type: "button", onClick: () => onSelect(c), onContextMenu: onContextRequest
                            ? (e) => {
                                e.preventDefault();
                                onContextRequest(c, e.clientX, e.clientY);
                            }
                            : undefined, style: {
                            width: "100%",
                            textAlign: "left",
                            padding: "4px 8px 4px 22px",
                            background: selected
                                ? "color-mix(in oklab, var(--accent-soft) 60%, var(--paper-2))"
                                : "transparent",
                            border: "none",
                            borderLeft: `2px solid ${selected ? "var(--accent)" : "transparent"}`,
                            borderRadius: 0,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: 13,
                            color: selected ? "var(--ink)" : "var(--ink-2)",
                            fontWeight: unread > 0 && !selected ? 600 : 400,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            minHeight: 24,
                            transition: "background 80ms ease",
                        }, onMouseEnter: (e) => {
                            if (!selected)
                                e.currentTarget.style.background = "var(--paper-3)";
                        }, onMouseLeave: (e) => {
                            if (!selected)
                                e.currentTarget.style.background = "transparent";
                        }, children: [_jsx("span", { style: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 14,
                                    color: selected ? "var(--accent-ink)" : "var(--ink-4)",
                                    fontFamily: "var(--mono)",
                                    fontSize: 12,
                                    flexShrink: 0,
                                }, children: "#" }), _jsx("span", { style: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }, children: c.name }), hasDraft && !selected && (_jsx("span", { title: "Draft", style: {
                                    color: "var(--accent)",
                                    fontSize: 14,
                                    lineHeight: 1,
                                    marginRight: -2,
                                }, children: "\u2022" })), badge] }), (() => {
                        const threads = threadsByChannel?.[c.id];
                        if (!threads || threads.length === 0 || !onSelectThread)
                            return null;
                        const expanded = expandedThreads.has(c.id);
                        if (!expanded) {
                            return (_jsxs("button", { type: "button", onClick: () => setExpandedThreads((prev) => new Set(prev).add(c.id)), style: threadRowStyle(false), children: [_jsx(ThreadGlyph, {}), _jsxs("span", { style: { color: "var(--ink-3)" }, children: [threads.length, " ", threads.length === 1 ? "thread" : "threads"] })] }));
                        }
                        return (_jsxs("ul", { style: { listStyle: "none", padding: 0, margin: 0 }, children: [threads.map((t) => {
                                    const tSelected = t.id === selectedThreadId;
                                    return (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => onSelectThread(c, t), style: threadRowStyle(tSelected), title: t.title, children: [_jsx(ThreadGlyph, {}), _jsx("span", { style: {
                                                        flex: 1,
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        color: tSelected ? "var(--ink)" : "var(--ink-3)",
                                                        textDecoration: t.resolved_at ? "line-through" : "none",
                                                    }, children: t.title }), t.resolved_at ? (_jsx("span", { title: "Resolved", style: { color: "var(--success)", fontSize: 11, flexShrink: 0 }, children: "\u2713" })) : (t.reply_count > 0 && (_jsx("span", { style: { color: "var(--ink-4)", fontSize: 10.5, fontFamily: "var(--mono)", flexShrink: 0 }, children: t.reply_count })))] }) }, t.id));
                                }), _jsx("li", { children: _jsx("button", { type: "button", onClick: () => setExpandedThreads((prev) => {
                                            const next = new Set(prev);
                                            next.delete(c.id);
                                            return next;
                                        }), style: threadRowStyle(false), children: _jsx("span", { style: { color: "var(--ink-4)", fontSize: 11 }, children: "collapse" }) }) })] }));
                    })()] }, c.id));
        }) }));
}
// KTK-397 — indented thread child row under a channel. Shares the channel
// row's hover/selection language at a smaller scale.
function threadRowStyle(selected) {
    return {
        width: "100%",
        textAlign: "left",
        padding: "2px 8px 2px 38px",
        background: selected
            ? "color-mix(in oklab, var(--accent-soft) 60%, var(--paper-2))"
            : "transparent",
        border: "none",
        borderLeft: `2px solid ${selected ? "var(--accent)" : "transparent"}`,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        gap: 6,
        minHeight: 20,
    };
}
function ThreadGlyph() {
    return (_jsx("svg", { width: "11", height: "11", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.4", "aria-hidden": "true", style: { color: "var(--ink-4)", flexShrink: 0 }, children: _jsx("path", { d: "M2 3h12M2 8h5M9.5 8H14M6 13h8" }) }));
}
/**
 * KTK-274 — single trailing badge slot for ChannelList rows. Three variants
 * keyed off the priority chain (decision > mention > unread). Mono 9.5px
 * pill, OKLCH tokens — matches the .wrow__badge family in web-shell.css.
 */
function ChannelBadge({ variant, title, children, }) {
    const palette = variant === "decision"
        ? { bg: "var(--decision)", fg: "var(--decision-ink)", border: "transparent", minWidth: 16 }
        : variant === "mention"
            ? { bg: "var(--accent)", fg: "var(--paper)", border: "transparent", minWidth: 16 }
            : { bg: "var(--paper-3)", fg: "var(--ink-2)", border: "var(--hairline)", minWidth: 16 };
    return (_jsx("span", { title: title, style: {
            fontFamily: "var(--mono)",
            fontSize: 9.5,
            fontWeight: 600,
            padding: "1px 6px",
            borderRadius: 999,
            letterSpacing: "0.02em",
            lineHeight: 1.4,
            background: palette.bg,
            color: palette.fg,
            border: `1px solid ${palette.border}`,
            minWidth: palette.minWidth,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
        }, children: children }));
}
//# sourceMappingURL=ChannelList.js.map